/**
 * Import job progress.
 *
 * REST is the source of truth. The WebSocket projection is best-effort — it can
 * lag or be absent — so it only ever moves a counter between polls and nudges
 * the poll when it claims the job settled; it never decides the job's state.
 *
 * Running progress is **indeterminate**: no total record count is known, and
 * `percent` reaches 100 only on `completed`/`completed_with_errors`. So a
 * running job gets an indeterminate bar rather than a fabricated percentage.
 *
 * `counts` are sparse — an absent key means zero — and they describe this run
 * only. `completed_with_errors` means every page was consumed but some rows
 * could not be merged, so the counts are surfaced rather than the whole run
 * being called a failure.
 */

import { useCallback } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RotateCcw, XCircle } from "lucide-react";
import { Button } from "components/ui/button";
import { toast } from "components/ui/toast";
import { cn } from "lib/utils";
import {
    describeFailure,
    isTerminalJob,
    useJob,
    useJobProgressHint,
    useRetryJob,
} from "core/features/integrations";
import type { ImportJob, JobCountKey, JobStatus } from "core/features/integrations";

const COUNT_LABEL: Record<JobCountKey, string> = {
    inserted: "Added",
    updated: "Updated",
    suppressed: "Suppressed",
    conflict: "Conflicts",
    permission_missing: "No permission",
    skipped: "Skipped",
};

/** Display order: what landed first, then what did not. */
const COUNT_ORDER: JobCountKey[] = [
    "inserted", "updated", "suppressed",
    "conflict", "permission_missing", "skipped",
];

const STATUS_COPY: Record<JobStatus, { label: string; detail: string }> = {
    queued: { label: "Queued", detail: "Waiting to start. Nothing has been imported yet." },
    running: { label: "Running", detail: "Importing. The total is not known in advance." },
    completed: { label: "Completed", detail: "Every page was imported." },
    completed_with_errors: {
        label: "Completed with errors",
        detail: "Every page was read, but some rows could not be merged. The counts below show what happened.",
    },
    failed: { label: "Failed", detail: "The import stopped before finishing." },
    cancelled: { label: "Cancelled", detail: "This run was cancelled." },
    paused_auth: {
        label: "Paused — reconnect needed",
        detail: "The provider connection needs attention. Reconnect, then save the profile again; this will not restart on its own.",
    },
    paused_quota: {
        label: "Paused — budget exhausted",
        detail: "The import budget is used up. An operator needs to review it; this will not restart on its own.",
    },
};

export interface ImportJobPanelProps {
    jobId: string;
    profileId: string;
    /** A retry returns a **new** job; the owner follows that one from then on. */
    onRetried?: (job: ImportJob) => void;
}

export function ImportJobPanel({ jobId, profileId, onRetried }: ImportJobPanelProps) {
    const { data: job, isLoading, terminal, refetch } = useJob(jobId);
    const hint = useJobProgressHint(jobId);
    const retry = useRetryJob();

    const handleRetry = useCallback(async () => {
        try {
            const next = await retry.mutateAsync({ jobId, profileId });
            // The old job stays terminal; progress now belongs to the new one.
            onRetried?.(next);
            toast({
                title: "Retry queued",
                description: "This starts a new full scan of the source.",
            });
        } catch (error) {
            const failure = describeFailure(error);
            toast({
                title: failure.code === "job_running" ? "Still running" : "Couldn’t retry",
                description: failure.message,
                variant: "error",
            });
        }
    }, [jobId, onRetried, profileId, retry]);

    if (isLoading && !job) {
        return (
            <section className="rounded-xl border border-line p-3">
                <p className="flex items-center gap-1.5 text-meta text-content-muted">
                    <Loader2
                        className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                        aria-hidden="true"
                    />
                    Checking the import…
                </p>
            </section>
        );
    }

    if (!job) return null;

    const copy = STATUS_COPY[job.status] ?? {
        label: job.status,
        // An unrecognised status is a refresh state, not an assumed success.
        detail: "This import is in a state this app does not recognise. Refresh to check.",
    };
    const running = job.status === "queued" || job.status === "running";
    const ok = job.status === "completed";
    const partial = job.status === "completed_with_errors";
    const bad = job.status === "failed" || job.status === "cancelled";

    /* Between polls the projection is the only thing that moves, so its
       `processed` is shown as a live count — never as a percentage. */
    const processed = hint?.processed;

    return (
        <section className="rounded-xl border border-line p-3">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h3 className="flex items-center gap-1.5 text-ui font-semibold text-content">
                        {ok && (
                            <CheckCircle2
                                className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
                                aria-hidden="true"
                            />
                        )}
                        {partial && (
                            <AlertTriangle
                                className="h-4 w-4 text-amber-600 dark:text-amber-400"
                                aria-hidden="true"
                            />
                        )}
                        {bad && (
                            <XCircle
                                className="h-4 w-4 text-red-600 dark:text-red-400"
                                aria-hidden="true"
                            />
                        )}
                        {running && (
                            <Loader2
                                className="h-4 w-4 animate-spin text-content-muted motion-reduce:animate-none"
                                aria-hidden="true"
                            />
                        )}
                        {copy.label}
                    </h3>
                    <p className="mt-0.5 text-meta text-content-muted">{copy.detail}</p>
                </div>

                {/* `sync_mode` is the server's choice and cannot be requested. */}
                <span className="shrink-0 rounded-md bg-surface-muted px-1.5 py-0.5 text-meta text-content-muted">
                    {job.sync_mode === "full" ? "Full scan" : "Incremental"}
                </span>
            </div>

            {running && (
                <div className="mt-3">
                    <div
                        className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-dark-700"
                        role="progressbar"
                        aria-label="Importing contacts"
                        aria-valuetext={
                            processed !== undefined ? `${processed} records so far` : "In progress"
                        }
                    >
                        {/* Indeterminate on purpose: no total is known. */}
                        <div className="h-full w-1/3 animate-pulse rounded-full bg-accent motion-reduce:animate-none" />
                    </div>
                    <p className="mt-1.5 text-meta tabular-nums text-content-muted">
                        {processed !== undefined
                            ? `${processed.toLocaleString()} records processed so far`
                            : "Progress will appear as records are processed."}
                    </p>
                </div>
            )}

            {(ok || partial || bad) && <JobCounts counts={job.counts} />}

            {job.error_code && (
                <p className="mt-2 text-meta text-content-muted">
                    Reported code: <span className="font-medium text-content">{job.error_code}</span>
                </p>
            )}

            <div className="mt-3 flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => void refetch()}>
                    Refresh
                </Button>
                {/* Retry is valid only for a terminal job, and starts a new scan. */}
                {terminal && isTerminalJob(job.status) && (
                    <Button
                        size="sm"
                        variant="secondary"
                        loading={retry.isPending}
                        onClick={() => void handleRetry()}
                    >
                        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                        Retry — new full scan
                    </Button>
                )}
            </div>
        </section>
    );
}

/** Sparse by contract: an absent key is zero, so only what came back is shown. */
function JobCounts({ counts }: { counts: ImportJob["counts"] }) {
    const present = COUNT_ORDER.filter((key) => counts[key] !== undefined);

    if (present.length === 0) {
        return (
            <p className="mt-2 text-meta text-content-muted">
                No per-row counts were reported for this run.
            </p>
        );
    }

    return (
        <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
            {present.map((key) => (
                <div key={key} className="flex items-baseline gap-1.5">
                    <dt className="text-meta text-content-muted">{COUNT_LABEL[key]}</dt>
                    <dd
                        className={cn(
                            "text-ui font-semibold tabular-nums",
                            key === "inserted" || key === "updated"
                                ? "text-content"
                                : "text-content-muted",
                        )}
                    >
                        {(counts[key] ?? 0).toLocaleString()}
                    </dd>
                </div>
            ))}
        </dl>
    );
}
